import styled from "styled-components";
import HeroBg from "/public/assets/images/products/analysis/service-bg.png";
import HeroMoBg from "/public/assets/images/products/analysis/service-mo-bg.png";
import Item01 from "/public/assets/images/products/analysis/service01.png";
import Item02 from "/public/assets/images/products/analysis/service02.png";
import Item03 from "/public/assets/images/products/analysis/service03.png";
import ServiceInfoDetailBg from "/public/assets/images/products/analysis/service-info-detail-bg.png";
import ServiceInfoDetailMoBg from "/public/assets/images/products/analysis/service-info-mo-detail-bg.png";
import LinkItemIcon from "/public/assets/icons/link-ico.png";
import { useContext, useEffect, useRef } from "react";
import { SIZES } from "@/components/constants";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getScroller, refreshOnAssetsReady, setupLenyStyle } from "@/app/homepage/utils";
import { LayoutContext } from "@/app/ClientRootHomepage";
import {useRouter} from "next/navigation";
import {useMediaQuery} from "react-responsive";

gsap.config({ force3D: false });

const Analysis = () => {
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });
    const { rootRef } = useContext(LayoutContext);
    const wrapRef = useRef(null);
    const router = useRouter();

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;

        const scroller = getScroller(rootRef);
        const contentEl =
            (scroller && scroller.querySelector && scroller.querySelector(".root")) ||
            (scroller && scroller.firstElementChild) ||
            scroller;

        const { cleanup } = setupLenyStyle({ wrap, scroller, contentEl });

        // 페이드 인 섹션
        const contentSections = Array.from(wrap.querySelectorAll(".section-content"));
        const triggers = [];
        const tweenItems = [];

        contentSections.forEach((item) => {
            gsap.set(item, { opacity: 0, y: 30, willChange: "transform, opacity" });

            const tweenGsap = gsap.to(item, {
                opacity: 1,
                y: 0,
                duration: 0.6,
                ease: "power2.out",
                paused: true,
                force3D: false,
            });

            const scrollGsap = ScrollTrigger.create({
                trigger: item,
                start: "top 70%",
                end: "top 45%",
                scroller,
                toggleActions: "play none none reverse",
                onEnter: () => tweenGsap.play(),
                onEnterBack: () => tweenGsap.play(),
                onLeaveBack: () => tweenGsap.reverse(),
            });

            tweenItems.push(tweenGsap);
            triggers.push(scrollGsap);
        });

        // 히어로 이미지 패럴랙스
        const secImgBox = wrap.querySelector(".hero-img-wrap");
        const secBgImg = secImgBox && secImgBox.querySelector("img");
        let parallaxTween = null;

        const buildParallax = () => {
            if (!secImgBox || !secBgImg) return;

            const scrollerEl = getScroller(rootRef);

            const boxHeight = secImgBox.clientHeight;
            const imgRect = secBgImg.getBoundingClientRect();
            const imgHeight = imgRect.height;

            const diff = Math.max(0, imgHeight - boxHeight);

            const baseRange = 100;
            const extraRange = diff * 0.8;
            const moveRange = Math.min(140, baseRange + extraRange);

            if (parallaxTween) {
                parallaxTween.scrollTrigger && parallaxTween.scrollTrigger.kill();
                parallaxTween.kill();
                parallaxTween = null;
            }
            parallaxTween = gsap.fromTo(
                secBgImg,
                { y: -moveRange / 2, willChange: "transform" },
                {
                    y: moveRange / 2,
                    ease: "none",
                    scrollTrigger: {
                        trigger: wrap,
                        start: "top bottom",
                        end: "bottom top",
                        scroller: scrollerEl,
                        scrub: 0.1,
                    },
                }
            );
        };

        const offAssets = refreshOnAssetsReady(wrap, buildParallax);
        const onResize = () => ScrollTrigger.refresh();

        window.addEventListener("resize", onResize);

        return () => {
            triggers.forEach((st) => st.kill());
            tweenItems.forEach((tw) => tw.kill());
            if (parallaxTween) {
                parallaxTween.scrollTrigger && parallaxTween.scrollTrigger.kill();
                parallaxTween.kill();
            }
            window.removeEventListener("resize", onResize);
            offAssets && offAssets();
            cleanup();
        };
    }, [rootRef, isTablet]);

    return (
        <ServiceInfoWrap ref={wrapRef}>
            <div className={"hero-img-wrap"}>
                <img
                    src={!isTablet ? HeroBg?.src : HeroMoBg?.src}
                    alt={"한눈에보기 이미지"}
                />
            </div>
            <div className={"content"}>
                <div className={"title-wrap"}>
                    <h2>Assay 종류</h2>
                </div>
                <div className={"cont-inner"}>
                    <div className={"item section-content"}>
                        <div className={"info-wrap"}>
                            <div className={"title"}>ELISA</div>
                            <div className={"desc"}>
                                <span>Quantikine® Gold standard</span>
                                <span>DuoSet The Best Development</span>
                                <span>그 외 다양한 ELISA kits</span>
                            </div>
                        </div>
                        <div className={"img-item"}><img src={Item01?.src} /></div>
                    </div>
                    <div className={"item section-content"}>
                        <div className={"info-wrap"}>
                            <div className={"title"}>Multiplex</div>
                            <div className={"desc"}>
                                <span>Luminex Correlated w/Quantikine</span>
                                <span>Ab Array Drug Treat 비교</span>
                                <span>Ella High Sensitive Multiplex ELISA</span>
                            </div>
                        </div>
                        <div className={"img-item"}><img src={Item02?.src} /></div>
                    </div>
                    <div className={"item section-content"}>
                        <div className={"info-wrap"}>
                            <div className={"title"}>Western Blot</div>
                            <div className={"desc"}>
                                <span>Automation Band image & Graph, Normalization & Quantification</span>
                                <span>4 Frees- Gel/ Blot/ Hands/ Imager</span>
                            </div>
                        </div>
                        <div className={"img-item"}><img src={Item03?.src} /></div>
                    </div>
                </div>
            </div>
            <ServiceInfoDetail>
                <div className={"list-inner"}>
                    <div className={"title section-content"}>
                        <h2>Unique <span>&</span> Guaranteed Quality</h2>
                    </div>
                    <div className={"list-item section-content"}>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>30년</span> 이상 축적된<br />
                                <span>R&D Systems 학술 노하우 적용</span>
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>Pre-test</span> (Trial) 의뢰 가능
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>정기적인 분석 장비 유지 관리</span><br />
                                (Daily, Weekly, Monthly 별)
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>1:1 맞춤 상담 및 실험</span> 디자인
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>Sample validation test 의뢰</span> 가능
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>Open lab</span> 운영 (실험 참관도 가능)
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                한눈에 보이는 <span>결과보고서</span>
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>철저한 검체 관리</span>
                            </p>
                        </div>
                        <div className={"item"}>
                            <p className={"tex-box"}>
                                <span>다양한 제조사 assay</span> (Multiplex, ELISA) 분석가능
                            </p>
                        </div>
                    </div>
                </div>
                <div className={"note-inner section-content"}>
                    <div className={"note-desc"}>
                        <div className={"title"}>
                            <h2>Sample 준비 주의사항</h2>
                        </div>
                        <div className={"desc-text-box"}>
                            <p>
                                정확한 sample 준비 방법은 kit의 datasheet를 참고하거나 웅비메디텍
                                <span className={"line"} onClick={() => {
                                    // router.push("/homepage/form?menu-id=691ff7d42df644bf3e6fdd93");
                                    window.open("mailto:techserv@woongbee.com", "_blank", "noopener,noreferrer");
                                }}>
                                    학술팀으로 문의 <span className={"line-icon-wrap"}><img src={LinkItemIcon?.src} alt={"문의 아이콘"}/></span>
                                </span>
                            </p>
                            <p>모든 sample은 냉동보관</p>
                            <p>2회 이상의 Freeze-thaw sample은 검사 결과에 영향을 줄 수 있습니다.</p>
                        </div>
                    </div>
                    <div className={"note-item"}>
                        <div className={"note-item-box"}>
                            <div className={"box-title"}>
                                <h3>Serum</h3>
                            </div>
                            <div className={"box-line"}></div>
                            <div className={"box-item"}>
                                <p>serum separator tube (SST)를 사용하여 sample을 실온에서 30분 동안 응고시킨 후 1000 x g에서 15분 동안 원심분리</p>
                                <p>바로 사용하거나 aliquot 하여 -20°C 이하 냉동보관</p>
                            </div>
                        </div>
                        <div className={"note-item-box"}>
                            <div className={"box-title"}>
                                <h3>Plasma</h3>
                            </div>
                            <div className={"box-line"}></div>
                            <div className={"box-item"}>
                                <p>각 kit별 권장하는 항응고제를 사용하여 sample 채취 후 30분 이내에 1000 x g에서 15분간 원심분리</p>
                                <p>바로 사용하거나 aliquot 하여 -20°C 이하 냉동보관</p>
                            </div>
                        </div>
                        <div className={"note-item-box"}>
                            <div className={"box-title"}>
                                <h3>
                                    {
                                        !isTablet ? (
                                        <>
                                            Cell culture<br />
                                            Supernates
                                        </>
                                    ) : (
                                        <>Cell culture Supernates</>
                                    )}
                                </h3>
                            </div>
                            <div className={"box-line"}></div>
                            <div className={"box-item"}>
                                <p>원심분리하여 particle 제거한 뒤, 바로 사용하거나 aliquot 하여 -20°C 이하 냉동보관</p>
                            </div>
                        </div>
                        <div className={"note-item-box"}>
                            <div className={"box-title"}>
                                <h3>
                                    {
                                        !isTablet ? (
                                        <>
                                            그 외<br />
                                            Sample type
                                        </>
                                    ) : (
                                        <>그 외 Sample type</>
                                    )}
                                </h3>
                            </div>
                            <div className={"box-line"}></div>
                            <div className={"box-item"}>
                                <p>웅비메디텍 학술팀으로
                                    <span className={"line"} onClick={() => {
                                        router.push("/homepage/form?menu-id=6948eeb5060a9b6eb364d78e");
                                    }}>
                                        문의하기
                                        <span className={"line-icon-wrap"}><img src={LinkItemIcon?.src} alt={"문의 아이콘"}/></span>
                                    </span>
                                </p>
                                <div className={"application"}>
                                <div className={"app-text"}
                                     onClick={() => {
                                         router.push("/homepage/form?menu-id=6948ef02060a9b6eb364d78f");
                                     }}>
                                    Open Lab 신청
                                </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ServiceInfoDetail>
        </ServiceInfoWrap>
    );
};

const ServiceInfoDetail = styled.div`
    background: url(${ServiceInfoDetailBg?.src}) no-repeat;
    background-size: cover;
    padding: 160px 144px;

    .list-inner {
        .title {
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            height: 112px;
            background: linear-gradient(90deg, rgba(43, 56, 168, 0.6) 0%, rgba(17, 22, 66, 0.6) 100%);
            border: 1px solid rgba(255, 255, 255, 0.4);
            backdrop-filter: blur(8px);
            border-radius: 99px;

            h2 {
                font-weight: 700;
                font-size: 32px;
                line-height: 48px;
                letter-spacing: -0.02em;
                color: #ffffff;

                span {
                    color: #5998f7;
                }
            }
        }

        .list-item {
            margin-top: 60px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 32px;

            .item {
                display: flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                height: 160px;
                background: rgba(26, 27, 38, 0.9);
                border: 1px solid rgba(255, 255, 255, 0.4);
                backdrop-filter: blur(2px);
                border-radius: 20px;
                padding: 20px 30px;

                p {
                    font-weight: 700;
                    font-size: 24px;
                    line-height: 36px;
                    text-align: center;
                    letter-spacing: -0.02em;
                    color: #fff;

                    span {
                        color: #a7d0fd;
                    }
                }
            }
        }
    }

    .note-inner {
        margin-top: 200px;
        display: flex;

        .note-desc {
            width: 35%;
            flex-shrink: 0;

            .title {
                padding: 32px 0 64px;

                h2 {
                    font-weight: 700;
                    font-size: 48px;
                    line-height: 58px;
                    letter-spacing: -0.02em;
                    color: #ffa007;
                }
            }

            .desc-text-box {
                padding-bottom: 32px;

                p {
                    font-weight: 700;
                    font-size: 20px;
                    line-height: 32px;
                    color: #ffffff;
                    padding-left: 15px;
                    position: relative;

                    & + p {
                        margin-top: 24px;
                    }

                    &::after {
                        content: "";
                        width: 5px;
                        height: 5px;
                        background-color: #fff;
                        border-radius: 50%;
                        position: absolute;
                        top: 13px;
                        left: 0;
                    }

                    .line {
                        cursor: pointer;
                        text-decoration: underline;
                        display: inline-flex;
                        align-items: start;
                        gap: 5px;
                        margin-left: 4px;

                        .line-icon-wrap{
                            width: 8px;
                            height: 8px;
                            display: flex;
                            margin-top: 8px;
                            align-items: center;
                        }
                    }
                }
            }
        }

        .note-item {
            .note-item-box {
                padding: 20px 30px;
                display: flex;
                gap: 24px;
                align-items: center;

                .box-title {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 160px;
                    height: 90px;
                    background: #323235;
                    border: 1px solid rgba(255, 255, 255, 0.4);
                    border-radius: 20px;
                    flex-shrink: 0;

                    h3 {
                        font-weight: 600;
                        font-size: 20px;
                        line-height: 30px;
                        display: flex;
                        text-align: center;
                        letter-spacing: -0.02em;
                        color: #ffffff;
                    }
                }

                .box-line {
                    width: 1px;
                    height: 56px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .box-item {
                    p {
                        font-weight: 400;
                        font-size: 18px;
                        line-height: 28px;
                        color: #ffffff;
                        position: relative;
                        padding-left: 13px;

                        &::after {
                            content: "";
                            width: 4px;
                            height: 4px;
                            background-color: #fff;
                            border-radius: 50%;
                            position: absolute;
                            top: 13px;
                            left: 0;
                        }

                        .line {
                            cursor: pointer;
                            text-decoration: underline;
                            display: inline-flex;
                            align-items: start;
                            gap: 5px;
                            margin-left: 4px;

                            .line-icon-wrap{
                                width: 8px;
                                height: 8px;
                                display: flex;
                                margin-top: 8px;
                                align-items: center;
                            }
                        }
                    }

                    .application {
                        margin-top: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 146px;
                        height: 44px;
                        background: #246ff2;
                        border-radius: 99px;
                        cursor: pointer;

                        .app-text {
                            font-weight: 600;
                            font-size: 16px;
                            line-height: 20px;
                            letter-spacing: 0.02em;
                            color: #ffffff;
                        }
                    }
                }
            }
        }
    }

    @media (max-width: ${SIZES.TABLET}) {
        background: url(${ServiceInfoDetailMoBg?.src}) no-repeat center / cover;
        padding: 120px 16px;

        .list-inner {
            .title {
                height: 84px;

                h2 {
                    font-size: 24px;
                    line-height: 36px;
                    letter-spacing: -0.02em;
                }
            }

            .list-item {
                display: block;
                margin-top: 40px;

                .item {
                    & + .item {
                        margin-top: 16px;
                    }

                    height: 88px;

                    p {
                        font-size: 16px;
                        line-height: 24px;
                        letter-spacing: -0.02em;
                    }
                }
            }
        }

        .note-inner {
            margin-top: 160px;
            display: block;

            .note-desc {
                width: 100%;

                .title {
                    padding: 32px 0 24px;

                    h2 {
                        font-size: 24px;
                        line-height: 36px;
                        text-align: center;
                        letter-spacing: -0.02em;
                    }
                }

                .desc-text-box {
                    p {
                        font-size: 16px;
                        line-height: 24px;
                        padding-left: 10px;

                        &::after {
                            width: 4px;
                            height: 4px;
                            top: 11px;
                        }

                        .line {
                            .line-icon-wrap{
                                margin-top: 5px;
                            }
                        }
                    }
                }
            }

            .note-item {
                margin-top: 64px;

                .note-item-box {
                    padding: 20px 8px 0;
                    display: block;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

                    .box-title {
                        width: fit-content;
                        padding: 0 30px;
                        height: 44px;

                        h3 {
                            font-size: 18px;
                            line-height: 28px;
                            letter-spacing: -0.02em;
                        }
                    }

                    .box-line {
                        display: none;
                    }

                    .box-item {
                        padding: 24px 0 64px;

                        p {
                            font-size: 14px;
                            line-height: 22px;
                            padding-left: 8px;

                            &::after {
                                width: 3px;
                                height: 3px;
                                top: 10px;
                            }

                            .line {
                                .line-icon-wrap{
                                    margin-top: 5px;
                                }
                            }
                        }

                        .application {
                            width: 125px;
                            height: 36px;
                            margin-left: 26px;

                            .app-text {
                                font-size: 14px;
                                line-height: 18px;
                                letter-spacing: 0.02em;
                            }
                        }
                    }
                }
            }
        }
    }
`;

const ServiceInfoWrap = styled.div`
    .hero-img-wrap {
        width: 100%;
        height: clamp(180px, 35vw, 300px);
        overflow: hidden;
        position: relative;

        img {
            display: block;
            width: 100%;
            height: 120%; 
            object-fit: cover;
            transform-origin: center center;
            will-change: transform;
        }
    }

    .content {
        padding: 100px 144px 160px;

        .title-wrap {
            padding: 24px 0;
            border-bottom: 1px solid #000000;

            h2 {
                font-weight: 700;
                font-size: 32px;
                line-height: 48px;
                letter-spacing: -0.02em;
                color: #000000;
            }
        }

        .cont-inner {
            .item {
                width: 100%;
                height: 248px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                border-bottom: 1px solid #cccccc;

                .info-wrap {
                    display: flex;
                    align-items: center;
                    padding-left: 40px;
                    gap: 24px;

                    .title {
                        width: 340px;
                        flex-shrink: 0;
                        font-weight: 700;
                        font-size: 24px;
                        line-height: 36px;
                        text-align: center;
                        letter-spacing: -0.02em;
                        color: #333333;
                    }

                    .desc {
                        span {
                            display: block;
                            font-weight: 400;
                            font-size: 18px;
                            line-height: 28px;
                            color: #666666;
                            position: relative;
                            padding-left: 15px;

                            &::before {
                                content: "";
                                width: 3px;
                                height: 3px;
                                border-radius: 50%;
                                background-color: #666;
                                position: absolute;
                                top: 50%;
                                transform: translateY(-50%);
                                left: 0;
                            }
                        }
                    }
                }

                .img-item {
                    width: 400px;
                }
            }
        }
    }

    @media (max-width: ${SIZES.TABLET}) {
        .content {
            padding: 40px 16px 80px;

            .title-wrap {
                padding: 16px 0;

                h2 {
                    font-size: 24px;
                    line-height: 36px;
                }
            }

            .cont-inner {
                .item {
                    display: block;
                    height: auto;
                    padding: 24px 0;

                    .info-wrap {
                        display: block;
                        padding: 0;
                        margin-bottom: 16px;

                        .title {
                            font-weight: 700;
                            font-size: 24px;
                            line-height: 36px;
                            margin-bottom: 16px;
                            text-align: left;
                        }

                        .desc {
                            span {
                                font-size: 16px;
                                line-height: 24px;
                                padding-left: 8px;
                            }
                        }
                    }

                    .img-item {
                        width: 100%;
                    }
                }
            }
        }
    }
`;

export default Analysis;
